// download-team-pack-and-write-manifest.ts
//
// Nguồn DUY NHẤT của quy tắc "version nào được ghi vào manifest" (#227).
//
// Trước #227 quy tắc này bị CHÉP ở 2 nơi (`commands/sync.ts` nhánh Supabase và
// `lib/team-pack-manager.ts` cho `avatar init`), cả hai cùng ưu tiên version THÔ
// người dùng gõ:  `version ?? objectToVersion(info.object)`. Gõ `--pack-version
// latest` ⇒ manifest nhận CHUỖI "latest" thay vì SemVer thật.
//
// Hệ quả: manifest là nguồn sự thật duy nhất về "đang cài bản nào", nên bẩn ở đây
// làm câm guard hạ cấp #191 ở lần sync KẾ TIẾP (`classifyPackVersionTransition`
// không parse được "latest" ⇒ "unknown" ⇒ im lặng), và làm `avatar status` +
// `sync --dry-run` hiển thị "latest" thay vì version thật.
//
// Quy tắc đúng: version ghi vào manifest LUÔN suy từ `info.object` server trả về.
// Edge fn `get-pack` coi `latest` như KHÔNG truyền và tự resolve qua
// `stable/latest.txt` (`get-pack/index.ts:41-54`) ⇒ `info.object` luôn có dạng
// `pack-<version>.tar.gz`. Với `--pack-version v1.1.40` (version cụ thể) thì
// server trả đúng object đó ⇒ giá trị không đổi so với trước.
import { join } from "node:path";

import { pathExists } from "./filesystem-helpers.js";
import {
  type ParsedPackManifest,
  readPackManifest,
  writePackManifest,
} from "./pack-manifest-store.js";
import { downloadAndExtractPack, fetchSignedPackUrl } from "./supabase-pack-downloader.js";
import { TEAM_PACK_MOUNT_DIRS } from "./symlink-farm-for-team-pack-mount-dirs.js";

// "pack-v0.2.0.tar.gz" → "v0.2.0"
export function objectToVersion(object: string): string {
  return object.replace(/^pack-/, "").replace(/\.tar\.gz$/, "");
}

export interface DownloadedTeamPack {
  installedVersion: string; // version THẬT đã cài (suy từ object name)
  downloadedObject: string; // vd "pack-v1.1.40.tar.gz"
  skipped: boolean; // true = bản trên đĩa đã trùng server, KHÔNG tải lại
}

export interface DownloadTeamPackOptions {
  // `avatar sync` bật; `avatar init` và `sync --reinstall` để tắt (luôn tải).
  skipIfUnchanged?: boolean;
}

// Bản trên đĩa trùng bản server ⇔ cùng object VÀ cùng sha256. Phải có sha256 ở CẢ hai
// phía: manifest do CLI cũ ghi (chưa lưu sha256) hoặc server không trả hash ⇒ coi là
// khác ⇒ tải như cũ. So cả sha256 (không chỉ tên) vì tarball có thể bị upload lại
// cùng tên với nội dung khác.
export function isInstalledPackSameAsServer(
  manifest: ParsedPackManifest | null,
  serverObject: string,
  serverSha256: string | undefined,
): boolean {
  if (!manifest?.sha256 || !serverSha256) return false;
  return manifest.downloadedObject === serverObject && manifest.sha256 === serverSha256;
}

// Sanity check rẻ trước khi tin manifest: trước đây mỗi `sync` giải nén lại nên tự chữa
// được pack bị xoá dở ngoài luồng tải (rm tay, antivirus...). Skip thì mất tính chất đó ⇒
// thiếu bất kỳ mount dir nào là coi như hỏng ⇒ tải lại. Không băm nội dung (tốn I/O).
async function packMountDirsPresent(packDir: string): Promise<boolean> {
  const checks = await Promise.all(TEAM_PACK_MOUNT_DIRS.map((d) => pathExists(join(packDir, d))));
  return checks.every(Boolean);
}

// Tải tarball pack + ghi manifest. `requestedVersion` chỉ đi tới SERVER (để pin
// bản), KHÔNG bao giờ đi vào manifest.
//
// `beforeDownload` chạy SAU khi biết version thật, TRƯỚC khi ghi đè thư mục pack —
// chỗ để `avatar sync` cảnh báo hạ cấp (#191) trong khi bản cũ còn nguyên trên đĩa.
//
// `skipIfUnchanged`: bỏ tải khi bản trên đĩa đã trùng server — mỗi lần tải là ~4MB
// egress Supabase (quota free 5GB/tháng), mà `avatar update` chạy `sync --all` cho
// MỌI workspace dù pack không đổi.
export async function downloadTeamPackAndWriteManifest(
  packDir: string,
  idToken: string,
  requestedVersion?: string,
  beforeDownload?: (resolvedVersion: string) => void | Promise<void>,
  options: DownloadTeamPackOptions = {},
): Promise<DownloadedTeamPack> {
  const info = await fetchSignedPackUrl(idToken, requestedVersion);
  const resolvedVersion = objectToVersion(info.object);

  if (options.skipIfUnchanged) {
    // Đọc manifest lỗi ⇒ không skip (fail-open về phía TẢI, không bao giờ kẹt bản hỏng).
    const manifest = await readPackManifest(packDir).catch(() => null);
    if (
      isInstalledPackSameAsServer(manifest, info.object, info.sha256) &&
      (await packMountDirsPresent(packDir))
    ) {
      return { installedVersion: resolvedVersion, downloadedObject: info.object, skipped: true };
    }
  }

  if (beforeDownload) await beforeDownload(resolvedVersion);

  await downloadAndExtractPack(info.url, packDir, info.sha256);
  await writePackManifest(packDir, {
    version: resolvedVersion,
    downloadedObject: info.object,
    // Lưu để lần sync sau so được với server (skipIfUnchanged). Chỉ ghi hash đã được
    // downloadAndExtractPack verify khớp tarball thật.
    ...(info.sha256 ? { sha256: info.sha256 } : {}),
    extractedAt: new Date().toISOString(),
  });

  return { installedVersion: resolvedVersion, downloadedObject: info.object, skipped: false };
}
